import { CircularProgress, Tab, Tabs } from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import React, { useEffect, useMemo, useState } from 'react'
import { createPortal } from 'react-dom'

import {
  getScorecardActions,
  makeScorecardActionWeek,
  markScorecardAction
} from 'api/teacherScorecard'

import {
  METRIC_LABEL,
  ORDER,
  listDates,
  raiseActions,
  SECTION_KINDS,
  resultsToSave
} from './actionRules'
import { C, fmtDate } from './Punctuality'
import { SECTIONS, addDays, band, scoresForDates, weekDays } from './scoring'

// Actions tab (27 Oct; simpler, 28 Oct after Shruti; 29 Oct): To do = every
// action at the centre for the week picked (Week of, ◀ ▶), each with her
// last 4 weeks and a one-line Log action; a week that is over is locked.
// Progress = one coloured grid per section, a dot where an action was
// logged. "Did it work?" removed (Satya, 29 Oct).

const GRID_WEEKS = 6

const BAND_STYLE = {
  good: { background: C.goodBg, color: C.good },
  ok: { background: C.okBg, color: C.ok },
  weak: { background: C.weakBg, color: C.weak },
  none: { background: '#F3F4F6', color: '#9AA0A8' }
}

const useStyles = makeStyles(() => ({
  chips: { display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 16 },
  chip: {
    minHeight: 36,
    padding: '0 16px',
    borderRadius: 999,
    border: `1px solid ${C.line}`,
    background: '#fff',
    font: 'inherit',
    fontSize: 14,
    cursor: 'pointer',
    color: '#3d4350'
  },
  chipOn: {
    background: '#E2572B',
    borderColor: '#E2572B',
    color: '#fff',
    fontWeight: 700
  },
  viewTabs: {
    minHeight: 40,
    marginBottom: 16,
    borderBottom: `1px solid ${C.line}`,
    '& .MuiTab-root': { minHeight: 40, minWidth: 0, textTransform: 'none' }
  },
  h2: { fontSize: 16, fontWeight: 800 },
  sub: { color: C.ink2, fontSize: 13 },
  // Normal case, never capitals (Satya, 28 Oct).
  lbl: { fontSize: 12.5, fontWeight: 600, color: C.ink2, marginBottom: 2 },
  since: { fontSize: 12, color: C.weak, fontWeight: 600 },
  err: { color: C.weak, fontSize: 13, marginBottom: 12 },
  tiles: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))',
    gap: 12,
    marginBottom: 16
  },
  tile: {
    border: `1px solid ${C.line}`,
    borderRadius: 10,
    padding: '12px 14px',
    background: '#fff'
  },
  tileNum: { fontSize: 26, fontWeight: 800 },
  card: {
    border: `1px solid ${C.line}`,
    borderRadius: 10,
    padding: '14px 18px',
    background: '#fff',
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
    marginBottom: 16,
    minWidth: 0
  },
  cardHead: {
    display: 'flex',
    flexWrap: 'wrap',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: 8
  },
  progress: { display: 'flex', alignItems: 'center', gap: 8, fontSize: 13.5 },
  progressBar: {
    width: 120,
    height: 8,
    borderRadius: 4,
    background: '#EEF0F2',
    overflow: 'hidden'
  },
  act: {
    display: 'grid',
    gridTemplateColumns:
      'minmax(150px, 1fr) minmax(200px, 1.6fr) minmax(200px, 1.6fr) 150px',
    gap: 16,
    alignItems: 'start',
    borderTop: `1px solid ${C.line}`,
    paddingTop: 12,
    fontSize: 13.5,
    '@media (max-width: 900px)': { gridTemplateColumns: '1fr' }
  },
  who: { fontSize: 14.5, fontWeight: 700 },
  pills: { display: 'flex', gap: 4, margin: '6px 0 2px' },
  pill: {
    fontSize: 12,
    fontWeight: 700,
    padding: '3px 9px',
    borderRadius: 999,
    whiteSpace: 'nowrap'
  },
  btn: {
    minHeight: 34,
    padding: '0 14px',
    borderRadius: 8,
    border: '1px solid #E2572B',
    background: '#E2572B',
    color: '#fff',
    font: 'inherit',
    fontSize: 13.5,
    fontWeight: 700,
    cursor: 'pointer',
    '&:disabled': { opacity: 0.6, cursor: 'default' }
  },
  ghost: { background: '#fff', color: '#B8431E', borderColor: C.line },
  // A plain link, not a loud button (Satya, 28 Oct).
  link: {
    padding: '6px 0 0',
    border: 0,
    color: '#B8431E',
    background: 'none',
    font: 'inherit',
    fontSize: 13,
    fontWeight: 600,
    cursor: 'pointer',
    '&:hover': { textDecoration: 'underline' }
  },
  doneTag: { color: C.good, fontWeight: 700 },
  log: {
    gridColumn: '1 / -1',
    background: '#FFF8F4',
    border: '1px solid #F1C7B6',
    borderRadius: 8,
    padding: '12px 14px',
    display: 'flex',
    flexDirection: 'column',
    gap: 10
  },
  logRow: { display: 'flex', flexWrap: 'wrap', gap: 8 },
  input: {
    flex: '1 1 280px',
    minHeight: 34,
    padding: '0 10px',
    border: `1px solid ${C.line}`,
    borderRadius: 8,
    font: 'inherit',
    fontSize: 13.5
  },
  item: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: 10,
    fontSize: 13.5,
    padding: '6px 0',
    borderTop: `1px solid ${C.line}`
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    fontSize: 13.5,
    background: '#fff'
  },
  th: {
    textAlign: 'left',
    padding: '10px 12px',
    background: C.band,
    fontWeight: 600,
    fontSize: 12.5,
    whiteSpace: 'nowrap'
  },
  td: {
    padding: '10px 12px',
    borderTop: `1px solid ${C.line}`,
    verticalAlign: 'top'
  },
  weekBar: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: 8,
    marginBottom: 16,
    fontSize: 15,
    fontWeight: 700
  },
  arrowBtn: {
    minWidth: 34,
    minHeight: 34,
    borderRadius: 8,
    border: `1px solid ${C.line}`,
    background: '#fff',
    font: 'inherit',
    cursor: 'pointer',
    '&:disabled': { opacity: 0.4, cursor: 'default' }
  },
  lock: { fontSize: 12.5, fontWeight: 600, color: C.ink2 },
  empty: { padding: 24, color: C.ink2, textAlign: 'center' },
  // Full width of the card, not bunched at the left (Satya, 28 Oct); plain
  // rows, no stripes from the page's table style.
  grid: {
    width: '100%',
    borderCollapse: 'separate',
    borderSpacing: 3,
    fontSize: 13,
    '& tr:nth-child(n)': { backgroundColor: 'transparent' }
  },
  gname: {
    width: '22%',
    padding: '6px 10px 6px 0',
    fontWeight: 600,
    whiteSpace: 'nowrap',
    textAlign: 'left'
  },
  gcell: {
    position: 'relative',
    minWidth: 62,
    padding: '7px 8px',
    borderRadius: 6,
    textAlign: 'center',
    fontWeight: 700,
    fontVariantNumeric: 'tabular-nums'
  },
  dot: {
    position: 'absolute',
    top: 3,
    right: 3,
    width: 7,
    height: 7,
    borderRadius: '50%',
    background: C.navy
  },
  avg: { '& td': { borderBottom: `2px solid ${C.line}` } },
  nameBtn: {
    padding: 0,
    border: 0,
    background: 'none',
    font: 'inherit',
    fontWeight: 600,
    cursor: 'pointer',
    color: 'inherit',
    textAlign: 'left',
    '&:hover': { textDecoration: 'underline' }
  }
}))

const shortDay = (iso) => fmtDate(iso, { day: 'numeric', month: 'short' })
const r1 = (v) => Math.round(v * 10) / 10

const ORDINAL = ['', '1st', '2nd', '3rd']
const nth = (n) => ORDINAL[n] || `${n}th`

const byUrgency = (a, b) =>
  ORDER[a.tier] - ORDER[b.tier] ||
  (a.value_at ?? 0) - (b.value_at ?? 0) ||
  a.who.localeCompare(b.who)

// Ticks from before 29 Oct carry the choice made then (Talked to her…).
const howOf = (a) => (a.done_how && a.done_how !== 'Done' ? a.done_how : '')

// On week W's list: raised by then, not closed before it, and not logged
// in an earlier week (a logged action stays on its log week only).
export const onWeek = (a, w) =>
  a.week_start <= w &&
  (!a.closed_week || a.closed_week > w) &&
  (!a.done_week || a.done_week >= w)

const change = (vals) => {
  const have = vals.filter((v) => v !== null && v !== undefined)
  if (have.length < 2) return null
  return r1(have[have.length - 1] - have[0])
}

function ChangeText({ d }) {
  if (d === null) return <span style={{ color: C.ink2 }}>—</span>
  if (d === 0) return <span style={{ color: C.ink2 }}>Same</span>
  return (
    <span style={{ color: d > 0 ? C.good : C.weak, fontWeight: 700 }}>
      {d > 0 ? 'Up' : 'Down'} {Math.abs(d).toFixed(1)}
    </span>
  )
}

// One grid per section: a row per teacher, a column per full week, the
// centre average on top (Satya, 28 Oct), a dot where an action was logged.
function WeekByWeek({ classes, teachers, weeks, actions, today, onOpen }) {
  const cols = useMemo(
    () => weeks.filter((m) => addDays(m, 6) < today).slice(-GRID_WEEKS),
    [weeks, today]
  )
  const scores = useMemo(
    () =>
      teachers
        .map((t) => ({
          t,
          by: cols.map((m) => scoresForDates(t, weekDays(m)))
        }))
        .sort((a, b) => a.t.name.localeCompare(b.t.name)),
    [teachers, cols]
  )
  const logged = useMemo(() => {
    const s = new Set()
    actions
      .filter((a) => a.done_week && a.employee_no)
      .forEach((a) => s.add(`${a.employee_no}|${a.metric}|${a.done_week}`))
    return s
  }, [actions])

  if (!cols.length)
    return <div className={classes.empty}>No full week to show yet.</div>

  const cell = (v, key, hasDot) => (
    <td
      key={key}
      className={classes.gcell}
      style={BAND_STYLE[band(v)]}
      title={hasDot ? 'Action logged this week' : undefined}
    >
      {v === null || v === undefined ? '–' : v.toFixed(1)}
      {hasDot && <span className={classes.dot} />}
    </td>
  )

  const grids = SECTIONS.map((s) => {
    const rows = scores.filter(({ by }) => by.some((x) => x[s.key] !== null))
    if (!rows.length) return null
    const avg = cols.map((_, i) => {
      const vals = rows.map(({ by }) => by[i][s.key]).filter((v) => v !== null)
      return vals.length
        ? r1(vals.reduce((a, b) => a + b, 0) / vals.length)
        : null
    })
    return (
      <div key={s.key} className={classes.card}>
        <span className={classes.h2}>{s.label}</span>
        <div style={{ overflowX: 'auto' }}>
          <table className={classes.grid}>
            <thead>
              <tr>
                <th className={`${classes.gname} ${classes.sub}`}>Teacher</th>
                {cols.map((m) => (
                  <th key={m} className={classes.sub}>
                    {shortDay(m)}
                  </th>
                ))}
                <th className={classes.sub} style={{ paddingLeft: 10 }}>
                  {GRID_WEEKS}-week change
                </th>
              </tr>
            </thead>
            <tbody>
              <tr className={classes.avg}>
                <td className={classes.gname}>Centre average</td>
                {avg.map((v, i) => cell(v, cols[i], false))}
                <td style={{ paddingLeft: 10 }}>
                  <ChangeText d={change(avg)} />
                </td>
              </tr>
              {rows.map(({ t, by }) => (
                <tr key={t.no}>
                  <td className={classes.gname}>
                    <button
                      type="button"
                      className={classes.nameBtn}
                      onClick={() =>
                        onOpen(
                          t.no,
                          SECTION_KINDS.includes(s.key) ? s.key : null
                        )
                      }
                    >
                      {t.name}
                    </button>
                  </td>
                  {by.map((x, i) =>
                    cell(
                      x[s.key],
                      cols[i],
                      logged.has(`${t.no}|${s.key}|${cols[i]}`)
                    )
                  )}
                  <td style={{ paddingLeft: 10 }}>
                    <ChangeText d={change(by.map((x) => x[s.key]))} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    )
  }).filter(Boolean)

  return (
    <>
      <div className={classes.chips} style={{ alignItems: 'center' }}>
        <span className={classes.pill} style={BAND_STYLE.good}>
          4 and above
        </span>
        <span className={classes.pill} style={BAND_STYLE.ok}>
          3 to 4
        </span>
        <span className={classes.pill} style={BAND_STYLE.weak}>
          under 3
        </span>
        <span className={classes.sub} style={{ display: 'flex', gap: 6 }}>
          <span className={classes.dot} style={{ position: 'static' }} /> action
          logged that week
        </span>
      </div>
      {grids.length ? (
        grids
      ) : (
        <div className={classes.empty}>No numbers for these weeks yet.</div>
      )}
    </>
  )
}

export default function Actions({
  teachers,
  allTeachers,
  weeks,
  complete,
  centre,
  today,
  onOpen,
  headSlot
}) {
  const classes = useStyles()
  const [view, setView] = useState('week')
  const [saved, setSaved] = useState(null)
  const [error, setError] = useState(null)
  const [busy, setBusy] = useState(false)
  // The Log action box open on one action: { id, note }.
  const [logging, setLogging] = useState(null)
  // The Monday picked in Week of (null = this week).
  const [picked, setPicked] = useState(null)

  const dates = useMemo(() => listDates(weeks, today), [weeks, today])

  // Load the actions; on the week's first visit (once every week is
  // loaded), make the week's list from every teacher and save the results
  // of logged actions whose freeze is over.
  useEffect(() => {
    let cancelled = false
    const load = async () => {
      let d = await getScorecardActions()
      if (!d.list_made && complete) {
        const items = raiseActions(allTeachers, dates).list.map((a) => ({
          kind: a.kind,
          metric: a.metric,
          tier: a.tier,
          centre: a.centre,
          employee_no: a.no,
          who: a.who,
          text: a.fact,
          todo: a.todo,
          value_at: a.value
        }))
        const results = resultsToSave(d.actions, allTeachers, weeks, today)
        d = await makeScorecardActionWeek({ items, results })
      }
      if (!cancelled) setSaved(d)
    }
    load().catch(
      () => !cancelled && setError('Could not load this week’s actions.')
    )
    return () => {
      cancelled = true
    }
  }, [complete, allTeachers, dates, weeks, today])

  const monday = saved && saved.week_start
  const week = picked || monday
  const locked = Boolean(monday && week < monday)

  // Weeks the toggle steps through: the first week with an action up to
  // this one.
  const weekChoices = useMemo(() => {
    if (!saved) return []
    const first = saved.actions.reduce(
      (m, a) => (a.week_start < m ? a.week_start : m),
      monday
    )
    const out = []
    for (let w = monday; w >= first; w = addDays(w, -7)) out.unshift(w)
    return out
  }, [saved, monday])
  const at = weekChoices.indexOf(week)

  const { good } = useMemo(
    () => raiseActions(teachers, week ? listDates(weeks, week) : dates),
    [teachers, weeks, week, dates]
  )

  // The full list for the centre (Satya, 29 Oct: "show the full list"):
  // every teacher there, data fixes and the CD · DCD · AM's own included.
  const shown = useMemo(
    () =>
      saved ? saved.actions.filter((a) => !centre || a.centre === centre) : [],
    [saved, centre]
  )

  const list = useMemo(
    () => (week ? shown.filter((a) => onWeek(a, week)).sort(byUrgency) : []),
    [shown, week]
  )
  const loggedCount = list.filter((a) => a.done_week === week).length

  const byNo = useMemo(
    () => Object.fromEntries(allTeachers.map((t) => [t.no, t])),
    [allTeachers]
  )
  // Her 4 full weeks before the week shown, on the action's number: the
  // weeks the list is made from, so the chips and the sentence agree.
  const recent = useMemo(
    () => weeks.filter((m) => week && m < week).slice(-4),
    [weeks, week]
  )
  const lastWeeks = (a) => {
    const t = byNo[a.employee_no]
    if (!t || !SECTIONS.some((s) => s.key === a.metric)) return null
    return recent.map((m) => ({
      m,
      v: scoresForDates(t, weekDays(m))[a.metric]
    }))
  }

  const replace = (row) =>
    setSaved((s) => ({
      ...s,
      actions: s.actions.map((x) => (x.id === row.id ? row : x))
    }))

  const saveLog = async () => {
    setBusy(true)
    setError(null)
    try {
      replace(
        await markScorecardAction(logging.id, {
          how: 'Done',
          note: logging.note.trim()
        })
      )
      setLogging(null)
    } catch (e) {
      setError('Could not save. Please try again.')
    } finally {
      setBusy(false)
    }
  }

  const actCard = (a) => {
    const last = lastWeeks(a)
    const open = logging && logging.id === a.id
    const done = a.done_week === week
    return (
      <div key={a.id} className={classes.act}>
        <div>
          <div className={classes.who}>{a.who}</div>
          {last && (
            <>
              <div className={classes.pills}>
                {last.map(({ m, v }) => (
                  <span
                    key={m}
                    className={classes.pill}
                    style={BAND_STYLE[band(v)]}
                    title={`Week of ${shortDay(m)}`}
                  >
                    {v === null ? '–' : v.toFixed(1)}
                  </span>
                ))}
              </div>
              <div className={classes.sub}>
                {METRIC_LABEL[a.metric]}, last 4 weeks
              </div>
            </>
          )}
          {a.round > 1 && (
            <div className={classes.since}>
              {nth(a.round)} time · first raised {shortDay(a.first_week)}
            </div>
          )}
          {!done && a.week_start < week && (
            <div className={classes.since}>
              On the list since {shortDay(a.week_start)}
            </div>
          )}
        </div>
        <div>
          <div className={classes.lbl}>What the numbers show</div>
          {a.text}
        </div>
        <div>
          <div className={classes.lbl}>Suggested step</div>
          {a.todo}
          {a.employee_no && (
            <div>
              <button
                type="button"
                className={classes.link}
                onClick={() =>
                  // Just this action's section (pun, mom, sub, plan); data
                  // fixes (link, class) open the whole pop-up.
                  onOpen(
                    a.employee_no,
                    SECTION_KINDS.includes(a.kind) ? a.kind : null
                  )
                }
              >
                View details
              </button>
            </div>
          )}
        </div>
        <div>
          {done && !open ? (
            <>
              <span className={classes.doneTag}>✓ {howOf(a) || 'Done'}</span>
              {a.done_note && <div className={classes.sub}>{a.done_note}</div>}
              {a.by && (
                <div className={classes.sub}>
                  {a.by} · {shortDay(a.done_at.slice(0, 10))}
                </div>
              )}
              {!locked && (
                <button
                  type="button"
                  className={`${classes.btn} ${classes.ghost}`}
                  style={{ marginTop: 6, minHeight: 28 }}
                  onClick={() =>
                    setLogging({ id: a.id, note: a.done_note || '' })
                  }
                >
                  Change
                </button>
              )}
            </>
          ) : (
            !open &&
            (locked ? (
              <span className={classes.lock}>Not logged</span>
            ) : (
              <button
                type="button"
                className={classes.btn}
                onClick={() => setLogging({ id: a.id, note: '' })}
              >
                Log action
              </button>
            ))
          )}
        </div>
        {open && (
          // One line of what was done, no choice buttons (Satya, 29 Oct).
          <div className={classes.log}>
            <b>
              What did you do
              {a.employee_no ? ` with ${a.who.split(' ')[0]}` : ''}?
            </b>
            <div className={classes.logRow}>
              <input
                className={classes.input}
                value={logging.note}
                maxLength={300}
                placeholder="What you did, in one line"
                autoFocus
                onChange={(e) => {
                  const note = e.target.value
                  setLogging((l) => ({ ...l, note }))
                }}
                onKeyDown={(e) => e.key === 'Enter' && saveLog()}
              />
              <button
                type="button"
                className={classes.btn}
                disabled={busy}
                onClick={saveLog}
              >
                {busy ? <CircularProgress size={14} color="inherit" /> : 'Save'}
              </button>
              <button
                type="button"
                className={`${classes.btn} ${classes.ghost}`}
                onClick={() => setLogging(null)}
              >
                Cancel
              </button>
            </div>
          </div>
        )}
      </div>
    )
  }

  const tiles = (rows) => (
    <div className={classes.tiles}>
      {rows.map(([name, n, color]) => (
        <div key={name} className={classes.tile}>
          <div className={classes.sub}>{name}</div>
          <div className={classes.tileNum} style={{ color }}>
            {n}
          </div>
        </div>
      ))}
    </div>
  )

  // Week of, with a toggle back to earlier weeks; a week that is over is
  // locked (Satya, 29 Oct).
  const weekBar = (
    <div
      className={classes.weekBar}
      style={headSlot ? { marginBottom: 0 } : undefined}
    >
      <button
        type="button"
        className={classes.arrowBtn}
        aria-label="Previous week"
        disabled={at <= 0}
        onClick={() => {
          setLogging(null)
          setPicked(weekChoices[at - 1])
        }}
      >
        ◀
      </button>
      <span>Week of {week ? shortDay(week) : ''}</span>
      <button
        type="button"
        className={classes.arrowBtn}
        aria-label="Next week"
        disabled={at < 0 || at >= weekChoices.length - 1}
        onClick={() => setPicked(weekChoices[at + 1])}
      >
        ▶
      </button>
      {locked && <span className={classes.lock}>🔒 Week over · view only</span>}
    </div>
  )

  if (!saved)
    return (
      <div className={classes.empty}>
        {error || (
          <>
            <CircularProgress size={20} />
            {!complete && (
              <div>This week’s list is made once every week has loaded.</div>
            )}
          </>
        )}
      </div>
    )

  return (
    <>
      {/* Week of sits in the page's header row beside Centre, where Period
          is on the other tabs (Satya, 29 Oct: no empty line above). */}
      {view === 'week' &&
        (headSlot ? createPortal(weekBar, headSlot) : weekBar)}
      {/* A slim tab row across the page, like the tabs above (Satya, 28 Oct). */}
      <Tabs
        value={view}
        onChange={(e, v) => setView(v)}
        indicatorColor="primary"
        className={classes.viewTabs}
      >
        <Tab value="week" label="To do" />
        <Tab value="grid" label="Progress" />
      </Tabs>
      {error && <div className={classes.err}>{error}</div>}

      {view === 'week' && (
        <>
          {tiles([
            [
              locked ? 'Logged that week' : 'Logged this week',
              `${loggedCount} of ${list.length}`
            ],
            ['Doing well', good.length, C.good]
          ])}

          <div className={classes.card}>
            <div className={classes.cardHead}>
              <span className={classes.h2}>
                {locked ? 'That week’s actions' : 'Act on these this week'}
              </span>
              <span className={classes.progress}>
                <span className={classes.progressBar}>
                  <span
                    style={{
                      display: 'block',
                      height: '100%',
                      background: C.good,
                      width: `${
                        list.length ? (100 * loggedCount) / list.length : 0
                      }%`
                    }}
                  />
                </span>
                {loggedCount} of {list.length} logged
              </span>
            </div>
            {!saved.list_made && !locked && (
              <div className={classes.sub}>
                This week’s list is made once every week has loaded.
              </div>
            )}
            {list.map(actCard)}
            {!list.length && (saved.list_made || locked) && (
              <div className={classes.empty}>
                Nothing to act on {locked ? 'that' : 'this'} week.
              </div>
            )}
          </div>

          <div className={classes.card}>
            <span className={classes.h2}>Doing well: tell them</span>
            {good.map((g) => (
              <div key={g.no} className={classes.item}>
                <b>{g.who}</b>
                <span className={classes.pill} style={BAND_STYLE.good}>
                  {g.overall.toFixed(1)} overall
                </span>
              </div>
            ))}
            {!good.length && (
              <div className={classes.sub}>No one at 4 or above yet.</div>
            )}
          </div>
          {!locked && (
            <div className={classes.sub}>
              Log what you did by Friday. Next Monday’s list checks the numbers
              again.
            </div>
          )}
        </>
      )}

      {view === 'grid' && (
        <WeekByWeek
          classes={classes}
          teachers={teachers}
          weeks={weeks}
          actions={shown}
          today={today}
          onOpen={onOpen}
        />
      )}
    </>
  )
}
